import HeroHeader from "components/sections/HeroHeader";
import DarkQuoteSection from "components/sections/DarkQuoteSection";
import HorizontalSpecs from "components/sections/HorizontalSpecs";
import MainContainer from "components/sections/MainContainer";
import MetaHead from "components/base/MetaHead";
import Trans from "components/base/Trans";
import HorizontalSlideSection from "components/sections/HorizontalSlideSection";
import Button from "components/base/Button";
import Image from "components/base/Image";
import CampaignPriceBlock, {
  getCampaignPrices,
} from "components/blocks/CampaignPriceBlock";
import { setGmdeCookie } from "utils/formHelper";

const hc50Schema = JSON.stringify({
  "@context": "https://schema.org",
  "@type": "Product",
  name: "HC50 Commercial Helicopter",
  description:
    "The HC50 is Hill Helicopters' certified commercial turbine helicopter, designed for commercial operations with the same airworthiness standards as the HX50.",
  image: "https://www.hillhelicopters.com/images/hc50/header.jpg",
  brand: {
    "@type": "Brand",
    name: "Hill Helicopters",
  },
  manufacturer: {
    "@type": "Organization",
    name: "Hill Helicopters",
    url: "https://www.hillhelicopters.com",
  },
  category: "Commercial Turbine Helicopter",
  model: "HC50",
  offers: {
    "@type": "Offer",
    priceCurrency: "GBP",
    price: String(getCampaignPrices("hc50").final),
    availability: "https://schema.org/PreOrder",
    url: "https://www.hillhelicopters.com/hc50",
    hasMerchantReturnPolicy: {
      "@type": "MerchantReturnPolicy",
      applicableCountry: "GB",
      returnPolicyCategory: "https://schema.org/MerchantReturnNotPermitted",
      merchantReturnDays: 0,
      description:
        "The pre-order deposit is strictly non-refundable. If the buyer cancels at any stage, the deposit is forfeited to Hill Helicopters as liquidated damages. If delivery is refused or delayed by the buyer for more than six months after the aircraft is completed, Hill Helicopters reserves the right to cancel the order and retain all payments already made.",
    },
    shippingDetails: {
      "@type": "OfferShippingDetails",
      shippingDestination: {
        "@type": "DefinedRegion",
        addressCountry: "GB",
      },
      shippingRate: {
        "@type": "MonetaryAmount",
        value: "0",
        currency: "GBP",
      },
      deliveryTime: {
        "@type": "ShippingDeliveryTime",
        handlingTime: {
          "@type": "QuantitativeValue",
          minValue: 0,
          maxValue: 0,
          unitCode: "DAY",
        },
        transitTime: {
          "@type": "QuantitativeValue",
          minValue: 0,
          maxValue: 0,
          unitCode: "DAY",
        },
      },
      description:
        "Aircraft is delivered Ex Works (EXW) at Hill Helicopters' factory in the United Kingdom. The tail boom and main rotor blades can be removed so the aircraft fits in a standard 20-foot shipping container. Export and onward shipping to the buyer's destination is arranged entirely by the buyer.",
    },
  },
  additionalProperty: [
    { "@type": "PropertyValue", name: "Cruise Speed", value: "140 kt" },
    { "@type": "PropertyValue", name: "Max Range", value: "700 NM" },
    { "@type": "PropertyValue", name: "Payload", value: "1,760 lb / 800 kg" },
    { "@type": "PropertyValue", name: "Engine Power", value: "500 shp" },
    { "@type": "PropertyValue", name: "Seats", value: "5" },
    { "@type": "PropertyValue", name: "Engine", value: "Hill GT50 Turboshaft" },
    {
      "@type": "PropertyValue",
      name: "Certification",
      value: "EASA CS-27 / FAA CFR Part 27",
    },
  ],
  url: "https://www.hillhelicopters.com/hc50",
});

export default function Hc50Page({ data }) {
  return (
    <MainContainer
      navStyle={{ gradient: false, inverted: false }}
      model="HC50"
      mandatoryFooter
    >
      <MetaHead
        title="LBL_HC50_META_TITLE"
        description="LBL_HC50_META_DESCRIPTION"
        imageUrl="/images/meta/image.jpg"
        structuredData={hc50Schema}
      />
      <HeroHeader
        imageSrc={{ base: "/images/hc50/header.jpg" }}
        alt="HC50 commercial turbine helicopter on the ground - Hill Helicopters"
        titleType="title_lg"
        align="flex-end"
        minHeight={["75vh", null, "600px"]}
        height={["75vh", null, "70vh"]}
        imageProps={{ objectPosition: "center" }}
        noScrollHint
        subtitleImg={
          <Image
            src="/images/hc50/HC50_logo.png"
            height={[0, 0, "38px"]}
            width="220px"
            objectFit="contain"
            mb="md"
          />
        }
        title={<Trans id="LBL_HC50_HERO_TITLE" />}
        description={<Trans id="LBL_HC50_HERO_DESCRIPTION" />}
        cta={
          <Button
            onClick={() =>
              document
                .getElementById("register")
                .scrollIntoView({ behavior: "smooth" })
            }
            trackLabel="im_interested_pre_order"
            mt={[4, null, 12]}
            variant="solid"
            size="regular"
          >
            <Trans id="LBL_CTA_IMINTERESTED" />
          </Button>
        }
      />
      <DarkQuoteSection
        title={
          <Image
            src="/images/hc50/HC50_logo.png"
            alt="HC50"
            height="38px"
            width="220px"
            objectFit="contain"
          />
        }
        description={<Trans id="LBL_HC50_SLIDE_1_DESCRIPTION" />}
      />
      <HorizontalSpecs
        isInverted
        pb={["120px", null, "240px"]}
        label1={
          <>
            <Trans id="LBL_TECHSPEC_VALUE_3" /> /{" "}
            <Trans id="LBL_TECHSPEC_VALUE_3A" />
          </>
        }
        value1={<Trans id="LBL_TECHSPEC_LABEL_3" />}
        label2={<Trans id="LBL_TECHSPEC_VALUE_4" />}
        value2={<Trans id="LBL_TECHSPEC_LABEL_4" />}
        label3={<Trans id="LBL_TECHSPEC_VALUE_1" />}
        value3={<Trans id="LBL_TECHSPEC_LABEL_1" />}
        label4={<Trans id="LBL_TECHSPEC_VALUE_2" />}
        value4={<Trans id="LBL_TECHSPEC_LABEL_2" />}
        content5={
          <CampaignPriceBlock model="hc50" compact variant="spec" w="260px" />
        }
      />
      <HorizontalSlideSection
        bg="black.900"
        baseSrc="/images/hc50/gallery/"
        items={[
          "01",
          "02",
          "03",
          "04",
          "05",
          "06",
          "07",
          "08",
          "09-vert",
          "10-vert",
          "11-vert",
          "12",
          "13",
          "14",
          "15",
          "16",
        ]}
      />
    </MainContainer>
  );
}

export async function getServerSideProps({ query, res, req }) {
  if (Object.keys(query).length > 0) {
    setGmdeCookie(query, res);
    return { redirect: { destination: "/hc50", permanent: true } };
  }
  const name = req.cookies.name || req.cookies.gmdeName || null;
  const email = req.cookies.email || req.cookies.gmdeEmail || null;
  const phone = req.cookies.phone || null;
  const phoneConfirmed = req.cookies.phone_confirmed || null;

  return { props: { data: { name, email, phone, phoneConfirmed } } };
}
